import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

import {
  CHUNK_RELOAD_STORAGE_KEY,
  canReloadAfterChunkError,
  isChunkLoadError,
} from '../helpers/chunk-load-error';

@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private lastReloadInMemory: number | null = null;

  start(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationError =>
            event instanceof NavigationError && isChunkLoadError(event.error),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.reloadOnce(event.url));
  }

  private reloadOnce(url: string): void {
    // Chrome reports a plain network failure with the same message as a deleted chunk.
    if (this.document.defaultView?.navigator.onLine === false) {
      return;
    }
    const now = Date.now();
    if (!canReloadAfterChunkError(this.readLastReload(), now)) {
      return;
    }
    // Without sessionStorage the guard would reset on every load and a chunk missing for good would loop.
    if (!this.writeLastReload(now)) {
      return;
    }
    const location = this.document.location;
    const target = new URL(url, location.href);
    if (target.origin !== location.origin) {
      location.reload();
      return;
    }
    location.assign(target.pathname + target.search + target.hash);
  }

  private readLastReload(): number | null {
    try {
      const value = this.document.defaultView?.sessionStorage.getItem(
        CHUNK_RELOAD_STORAGE_KEY,
      );
      const parsed = value ? Number(value) : NaN;
      return Number.isFinite(parsed) ? parsed : this.lastReloadInMemory;
    } catch {
      return this.lastReloadInMemory;
    }
  }

  private writeLastReload(now: number): boolean {
    this.lastReloadInMemory = now;
    try {
      const storage = this.document.defaultView?.sessionStorage;
      if (!storage) {
        return false;
      }
      storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
      return true;
    } catch {
      return false;
    }
  }
}
